import React, { useMemo, useState } from "react"
import { faCalendar, faClose, faHashtag, faLink, faMoneyBill, faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { EAccountPlansType } from "../../Enum/EAccountPlansType"
import { ECommercialOriginType } from "../../Enum/ECommercialOriginType"
import { AdminSubscriptionCase } from "../../Objects/AdminDashboard/AdminSubscriptionCase"
import { AdminUpdateSubscription } from "../../Objects/AdminDashboard/AdminUpdateSubscription"
import { useTranslation } from "react-i18next"

interface AdminSubscriptionEditPopupProps {
  subscription: AdminSubscriptionCase
  onClose: () => void
  onSave: (subscriptionId: number, request: AdminUpdateSubscription) => void
}

const toInputDate = (value?: string) => (value ? value.split("T")[0] : "")

const toDatePayload = (value: string) => (value ? new Date(`${value}T12:00:00.000Z`).toISOString() : undefined)

const toMoneyNumber = (value: string) => {
  const normalized = value.replace(",", ".").trim()

  const parsed = normalized ? Number.parseFloat(normalized) : 0

  return Number.isFinite(parsed) ? parsed : 0
}

const planOptions = Object.values(EAccountPlansType)
  .filter((value) => typeof value === "number")
  .map((value) => ({ value: String(value), label: (value as number).toEAccountPlansTypeToString() }))

const originOptions = Object.values(ECommercialOriginType)
  .filter((value) => typeof value === "number")
  .map((value) => ({ value: String(value), label: (value as number).toECommercialOriginTypeString() }))

export const AdminSubscriptionEditPopup: React.FC<AdminSubscriptionEditPopupProps> = ({
  subscription,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation("frontend")

  const [form, setForm] = useState(new AdminUpdateSubscription(subscription))

  const [nextPaymentDate, setNextPaymentDate] = useState(toInputDate(subscription.nextPaymentDate))

  const [lastPaymentDate, setLastPaymentDate] = useState(toInputDate(subscription.lastPaymentDate))

  const statusOptions = useMemo(
    () => [
      { value: "none", label: t("admin_dashboard.subscription_edit.status_none") },
      { value: "pending", label: t("admin_dashboard.subscription_edit.status_pending") },
      { value: "in_process", label: t("admin_dashboard.subscription_edit.status_in_process") },
      { value: "authorized", label: t("admin_dashboard.subscription_edit.status_authorized") },
      { value: "payment_required", label: t("admin_dashboard.subscription_edit.status_payment_required") },
      { value: "paused", label: t("admin_dashboard.subscription_edit.status_paused") },
      { value: "cancelled", label: t("admin_dashboard.subscription_edit.status_cancelled") },
    ],
    [t],
  )

  const paymentStatusOptions = useMemo(
    () => [
      { value: "", label: t("admin_dashboard.subscription_edit.payment_status_empty") },
      { value: "approved", label: t("admin_dashboard.subscription_edit.payment_status_approved") },
      { value: "pending", label: t("admin_dashboard.subscription_edit.payment_status_pending") },
      { value: "in_process", label: t("admin_dashboard.subscription_edit.payment_status_in_process") },
      { value: "rejected", label: t("admin_dashboard.subscription_edit.payment_status_rejected") },
      { value: "cancelled", label: t("admin_dashboard.subscription_edit.payment_status_cancelled") },
      { value: "refunded", label: t("admin_dashboard.subscription_edit.payment_status_refunded") },
      { value: "charged_back", label: t("admin_dashboard.subscription_edit.payment_status_charged_back") },
    ],
    [t],
  )

  const patch = (data: Partial<AdminUpdateSubscription>) => setForm(new AdminUpdateSubscription({ ...form, ...data }))

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    onSave(
      subscription.subscriptionId,
      new AdminUpdateSubscription({
        ...form,
        nextPaymentDate: toDatePayload(nextPaymentDate),
        lastPaymentDate: toDatePayload(lastPaymentDate),
        commercialReferralCode: form.commercialReferralCode?.trim().toUpperCase(),
        providerName: form.providerName?.trim(),
      }),
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("admin_dashboard.subscription_edit.title")}
              icon={faPenToSquare}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.button.onClose"
                />
              }
            />

            <form onSubmit={handleSubmit} className="mt-3 space-y-3">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-800">
                  {t("admin_dashboard.subscription_edit.warning")}
                </div>
              </Animate>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 p-2">
                <GenericDropDown
                  data={planOptions}
                  initialState={String(form.planType)}
                  handlerFuncion={(event) => patch({ planType: Number(event.target.value) as EAccountPlansType })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.select.select"
                />

                <GenericDropDown
                  data={statusOptions}
                  initialState={form.subscriptionStatus || "none"}
                  handlerFuncion={(event) => patch({ subscriptionStatus: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.select.select2"
                />

                <InputField
                  type="number"
                  placeholder={t("admin_dashboard.subscription_edit.monthly_amount")}
                  onInvalidMessage={t("admin_dashboard.subscription_edit.monthly_amount_invalid")}
                  icon={faMoneyBill}
                  value={String(form.currentChargeAmount || "")}
                  required
                  onChange={(event) => patch({ currentChargeAmount: Math.max(0, toMoneyNumber(event.target.value)) })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.input.monthlyAmount"
                />

                <GenericDropDown
                  data={[
                    { value: "", label: t("admin_dashboard.subscription_edit.status_detail_empty") },
                    {
                      value: "manually_activated",
                      label: t("admin_dashboard.subscription_edit.status_detail_manually_activated"),
                    },
                    {
                      value: "admin_adjusted",
                      label: t("admin_dashboard.subscription_edit.status_detail_admin_adjusted"),
                    },
                    {
                      value: "payment_reconciled",
                      label: t("admin_dashboard.subscription_edit.status_detail_payment_reconciled"),
                    },
                    {
                      value: "provider_sync_reviewed",
                      label: t("admin_dashboard.subscription_edit.status_detail_provider_sync_reviewed"),
                    },
                    {
                      value: "subscription_cancelled",
                      label: t("admin_dashboard.subscription_edit.status_detail_subscription_cancelled"),
                    },
                  ]}
                  initialState={form.statusDetail ?? ""}
                  handlerFuncion={(event) => patch({ statusDetail: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.select.select3"
                />

                <InputField
                  type="date"
                  placeholder={t("admin_dashboard.subscription_edit.next_payment_date")}
                  icon={faCalendar}
                  value={nextPaymentDate}
                  onChange={(event) => setNextPaymentDate(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.input.nextPaymentDate"
                />

                <GenericDropDown
                  data={paymentStatusOptions}
                  initialState={form.lastPaymentStatus ?? ""}
                  handlerFuncion={(event) => patch({ lastPaymentStatus: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.select.select4"
                />

                <InputField
                  type="date"
                  placeholder={t("admin_dashboard.subscription_edit.last_payment_date")}
                  icon={faCalendar}
                  value={lastPaymentDate}
                  onChange={(event) => setLastPaymentDate(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.input.lastPaymentDate"
                />

                <GenericDropDown
                  data={originOptions}
                  initialState={String(form.commercialOriginType)}
                  handlerFuncion={(event) => {
                    const commercialOriginType = Number(event.target.value) as ECommercialOriginType

                    patch({
                      commercialOriginType,
                      commercialReferralCode:
                        commercialOriginType === ECommercialOriginType.Reseller
                          ? form.commercialReferralCode
                          : "",
                    })
                  }}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.select.select5"
                />

                <InputField
                  type="text"
                  placeholder={t("admin_dashboard.subscription_edit.commercial_referral_code")}
                  icon={faHashtag}
                  value={form.commercialReferralCode ?? ""}
                  disable={form.commercialOriginType !== ECommercialOriginType.Reseller}
                  required={form.commercialOriginType === ECommercialOriginType.Reseller}
                  maxLength={30}
                  onChange={(event) => patch({ commercialReferralCode: event.target.value.toUpperCase() })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.input.commercialReferralCode"
                />

                <InputField
                  type="text"
                  placeholder={t("admin_dashboard.subscription_edit.provider_name")}
                  icon={faLink}
                  value={form.providerName ?? ""}
                  maxLength={50}
                  onChange={(event) => patch({ providerName: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.input.providerName"
                />
              </div>

              <div className="flex flex-col-reverse gap-2 p-2 md:flex-row md:justify-end">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  onClick={onClose}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.button.onClose2"
                >
                  {t("admin_dashboard.subscription_edit.cancel")}
                </button>

                <button
                  type="submit"
                  className="rounded-lg border border-orange-500 bg-orange-600 px-4 py-2 text-sm font-bold text-white hover:bg-orange-700"
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminSubscriptionEditPopup.button.save"
                >
                  {t("admin_dashboard.subscription_edit.save")}
                </button>
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
